/**
 * Small green and red notices that confirm an action worked or say why it did
 * not, for a few seconds, wherever the person is on the page.
 *
 * Asked for on 2026-10-12: the console said nothing when a write went through —
 * and «Eliminar demo»'s result dialog closed the instant it opened, because the
 * organization it lived on was gone. A module-level channel rather than a
 * context, so the query cache (`main.tsx`), which lives outside React, can
 * raise one; `<Toaster />` is the one listener that draws them.
 */
export type ToastSeverity = 'success' | 'error' | 'info'

export interface Toast {
  id: number
  severity: ToastSeverity
  message: string
}

type Listener = (t: Toast) => void

const listeners = new Set<Listener>()
let nextId = 1

function emit(severity: ToastSeverity, message: string) {
  const t = { id: nextId++, severity, message }
  listeners.forEach((l) => l(t))
}

export const toast = {
  success: (message: string) => emit('success', message),
  error: (message: string) => emit('error', message),
  info: (message: string) => emit('info', message),
}

/** For `<Toaster />`; returns the unsubscribe. */
export function subscribeToasts(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * What a mutation says when it succeeds, declared on the hook as `meta.toast`
 * (the query cache in `main.tsx` reads it):
 *
 * - `'preview'` — a proposal: whether it waits for a second person or can be
 *   applied now, from the server's `requires_approval`;
 * - `'confirm'` — applied, or already applied (a replay);
 * - a sentence — said as is;
 * - absent — nothing: the screen answers for itself.
 */
export type ToastMeta = 'preview' | 'confirm' | string

export function successMessage(meta: ToastMeta | undefined, data: unknown): string | null {
  if (!meta) return null
  const d = (data ?? {}) as { requires_approval?: boolean; replayed?: boolean }
  if (meta === 'preview') {
    return d.requires_approval === false
      ? 'Propuesta lista: revísala y aplícala.'
      : 'Propuesta creada. Falta que otra persona la apruebe en Plataforma → Cambios.'
  }
  if (meta === 'confirm') {
    return d.replayed ? 'Ya estaba aplicado.' : 'Aplicado. Queda en la bitácora.'
  }
  return meta
}
